
import {
  ArrowUpRight,
  Mail,
  MapPin,
  Phone,
  Linkedin,
  Instagram,
  MessageCircle,
} from "lucide-react";

import logo from "../assets/logo2.png";
import logoName from "../assets/logoname.png";

const companyMapsLink =
  "https://maps.app.goo.gl/j2VKqhBRCHWttohw9";

const companyAddress =
  "No 11, 9/8, Poompukar Nagar, Thudiyalur, Coimbatore, Tamil Nadu 641034";

const companyMapsEmbed =
  "https://maps.google.com/maps?q=5%2F52%2C%20Main%20Street%2C%20near%20Krishna%20Hall%2C%20VKL%20Nagar%2C%20Thudiyalur%2C%20Coimbatore%2C%20Tamil%20Nadu%20641034&z=15&output=embed";

const footerLinks = {
  company: [
    { name: "About", href: "#about" },
    { name: "Services", href: "#products" },
    { name: "Insights", href: "#insights" },
    { name: "Careers", href: "#careers" },
  ],
  support: [
    { name: "Contact", href: "#contact" },
    { name: "Privacy Policy", href: "#" },
    { name: "Terms of Use", href: "#" },
    { name: "FAQ", href: "#" },
  ],
  social: [
    {
      name: "WhatsApp",
      href: "https://wa.me/918110054916",
      icon: MessageCircle,
    },
    {
      name: "Instagram",
      href: "https://www.instagram.com/balvion_tech?igsh=MXBycm9lMjNuZmY2dg==",
      icon: Instagram,
    },
    {
      name: "LinkedIn",
      href: "https://www.linkedin.com/company/balvion-technologies/",
      icon: Linkedin,
    },
  ],
};

export default function Footer() {
  return (
    <footer
      id="footer"
      className="relative overflow-hidden border-t border-sky-100 bg-[linear-gradient(180deg,#f8fbff_0%,#eff6ff_100%)] px-6 py-12 sm:py-14"
    >
      {/* Background decoration */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[8%] top-10 h-56 w-56 rounded-full bg-sky-200/30 blur-3xl" />
        <div className="absolute bottom-0 right-[10%] h-64 w-64 rounded-full bg-cyan-200/25 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl">
        {/* Footer columns */}
        <div className="grid grid-cols-1 gap-x-7 gap-y-10 sm:grid-cols-2 lg:grid-cols-[1.35fr_0.65fr_0.7fr_0.95fr_1.15fr] lg:gap-x-6">

          {/* Company information */}
          <div className="min-w-0">
            <a href="#hero" className="inline-flex items-center gap-3">
              <img
                src={logo}
                alt="Balvion logo"
                className="h-12 w-12 object-contain"
              />
              <img
                src={logoName}
                alt="Balvion Technologies"
                className="h-7 w-auto object-contain"
              />
            </a>

            <p className="mt-4 max-w-sm text-sm leading-6 text-slate-600">
              Balvion Technologies builds robotics education, innovation
              platforms, HR services, and future-ready learning experiences.
            </p>

            <div className="mt-5 space-y-3 text-sm text-slate-600">
              <a
                href={companyMapsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 transition-colors hover:text-sky-700"
              >
                <MapPin
                  size={16}
                  className="mt-0.5 shrink-0 text-sky-600"
                />
                <span>{companyAddress}</span>
              </a>

              <div className="flex items-start gap-2">
                <Phone
                  size={16}
                  className="mt-0.5 shrink-0 text-sky-600"
                />
                <span>
                  +91 88380 54916
                  <br />
                  +91 81100 54916
                </span>
              </div>

              <a
                href="mailto:hr@balviontech.com"
                className="flex items-center gap-2 transition-colors hover:text-sky-700"
              >
                <Mail size={16} className="shrink-0 text-sky-600" />
                <span className="break-all">hr@balviontech.com</span>
              </a>

              <a
                href="mailto:admin@balviontech.com"
                className="flex items-center gap-2 transition-colors hover:text-sky-700"
              >
                <Mail size={16} className="shrink-0 text-sky-600" />
                <span className="break-all">admin@balviontech.com</span>
              </a>
            </div>
          </div>

          {/* Company links */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-900">
              Company
            </h3>

            <ul className="mt-5 space-y-3 text-sm">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-slate-600 transition-colors hover:text-sky-700"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Support links */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-900">
              Support
            </h3>

            <ul className="mt-5 space-y-3 text-sm">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-slate-600 transition-colors hover:text-sky-700"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect */}
          <div className="min-w-0">
            <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-900">
              Connect
            </h3>

            <p className="mt-4 text-sm leading-6 text-slate-600">
              Follow our updates, events, and innovation stories.
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              {footerLinks.social.map((item) => {
                const Icon = item.icon;

                return (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={item.name}
                    title={item.name}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-sky-100 bg-white text-slate-600 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-sky-300 hover:text-sky-700"
                  >
                    <Icon size={18} />
                  </a>
                );
              })}
            </div>

            <a
              href="#contact"
              className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-sky-700"
            >
              Send an inquiry
              <ArrowUpRight size={15} />
            </a>
          </div>

          {/* Compact Google Maps preview */}
          <div className="min-w-0">
            <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-900">
              Find Us
            </h3>

            <p className="mt-4 text-sm leading-6 text-slate-600">
              Visit Balvion Technologies, Coimbatore.
            </p>

            <a
              href={companyMapsLink}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open Balvion Technologies directions in Google Maps"
              className="group relative mt-4 block overflow-hidden rounded-xl border border-sky-100 bg-white shadow-[0_8px_24px_rgba(15,23,42,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_14px_30px_rgba(15,23,42,0.14)]"
            >
              <iframe
                title="Balvion Technologies map preview"
                src={companyMapsEmbed}
                className="pointer-events-none block h-36 w-full sm:h-40 lg:h-36"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                style={{ border: 0 }}
              />

              <div className="flex items-center justify-between gap-2 border-t border-sky-100 bg-sky-50 px-3 py-2.5">
                <span className="flex items-center gap-2 text-sm font-medium text-slate-700 transition-colors group-hover:text-sky-700">
                  <MapPin size={16} className="text-sky-600" />
                  Get directions
                </span>
                <ArrowUpRight
                  size={16}
                  className="shrink-0 text-sky-600"
                />
              </div>
            </a>

            <p className="mt-2 text-xs leading-5 text-slate-500">
              Thudiyalur, Coimbatore, Tamil Nadu
            </p>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-10 flex flex-col gap-3 border-t border-sky-100 pt-6 text-sm text-slate-500 md:flex-row md:items-center md:justify-between">
          <p>© 2026 Balvion Technologies. All rights reserved.</p>
          <p>Robotics • Education • HR Services • Innovation</p>
        </div>
      </div>
    </footer>
  );
}
